<template>
  <!-- With the hardened path on, success is only shown after the server confirms payment. -->
  <div v-if="!verified" class="success-container">
    <div class="success-card">
      <h2>Chưa xác minh được thanh toán</h2>
      <p>Máy chủ chưa xác nhận khoản chuyển cho đơn này. Vui lòng kiểm tra lại ở trang thanh toán; nếu bạn đã chuyển khoản, đừng chuyển thêm.</p>
      <button class="go-home-btn" type="button" @click="goHome">Về trang đặt vé</button>
    </div>
  </div>
  <div v-else class="success-container">
    <!-- Animated celebration background -->
    <div class="success-bg-particles"></div>

    <FadeContent :duration="600" :translateY="30">
    <div class="success-card">
      <div class="success-icon">
        <div class="success-icon-ring"></div>
        <i class="fas fa-check-circle"></i>
      </div>
      <h1>
        <GradientText
          text="Thanh toán thành công!"
          :colors="['#16a34a', '#15803d', '#38bdf8', '#16a34a']"
          :animationSpeed="6"
        />
      </h1>
      <p>Đơn đặt vé của bạn đã được xác nhận.</p>

      <div class="booking-details">
        <h3>Chi tiết đặt vé</h3>
        <div
          v-if="!isLoading && bookingInfo && bookingInfo.length > 0"
          class="booking-info"
        >
          <div
            v-for="(booking, index) in bookingInfo"
            :key="booking.booking_id"
            class="booking-item"
          >
            <h4 v-if="bookingInfo.length > 1">Vé {{ index + 1 }}</h4>
            <p><strong>Mã đặt vé:</strong> DCX{{ booking.booking_id }}</p>
            <p>
              <strong>Khách hàng:</strong> {{ booking.customer_name }} -
              {{ booking.customer_phone }}
            </p>
            <p><strong>Điểm đến:</strong> {{ booking.destination || 'N/A' }}</p>
            <p>
              <strong>Thời gian:</strong>
              {{ formatTime(booking.departure_time) }}
            </p>
            <p><strong>Loại vé:</strong> {{ booking.ticket_type?.type }}</p>
            <p><strong>Giá vé:</strong> {{ formatCurrency(booking.price) }}</p>
            <p v-if="!isSpecialTicketTypeForBooking(booking)">
              <strong>Số ghế:</strong>
              {{ booking.seat_numbers?.join(', ') || 'N/A' }}
            </p>
            <div
              v-if="isSpecialTicketTypeForBooking(booking)"
              class="special-ticket-note"
            >
              <i class="fas fa-info-circle"></i> Vé đặc biệt, không cần chọn ghế
            </div>
          </div>

          <!-- Thêm section đơn hàng phụ trợ -->
          <div v-if="addonProducts && addonProducts.length > 0" class="addon-section">
            <h3>🛍️ Đơn hàng phụ trợ</h3>
            <div v-if="hasGroupBooking" class="group-notice">
              <i class="fas fa-info-circle"></i>
              <span>Bạn thuộc nhóm đặt vé chung, dưới đây là tất cả đơn hàng phụ trợ của nhóm:</span>
            </div>
            
            <div class="addon-list">
              <div
                v-for="addon in addonProducts"
                :key="`${addon.booking_id}_${addon.addon_id}`"
                class="addon-item"
              >
                <div class="addon-header">
                  <h4>{{ addon.name }}</h4>
                </div>
                <div class="addon-details">
                  <p><i class="fas fa-align-left"></i> {{ addon.description }}</p>
                  <p><i class="fas fa-box"></i> Số lượng: <strong>{{ addon.quantity }}</strong></p>
                  <p><i class="fas fa-money-bill-wave"></i> Đơn giá: <span class="price">{{ formatCurrency(addon.price) }}</span></p>
                  <p><i class="fas fa-calculator"></i> Thành tiền: <span class="total-price">{{ formatCurrency(addon.total_price) }}</span></p>
                  <p class="ordered-by"><i class="fas fa-user"></i> Đặt bởi: {{ addon.ordered_by }} (DCX{{ addon.booking_id }})</p>
                </div>
              </div>
            </div>

            <div class="addon-total">
              <strong>💰 Tổng giá trị đơn hàng phụ trợ: <span class="total-amount">{{ formatCurrency(totalAddonCost) }}</span></strong>
            </div>
          </div>

          <div v-if="hasZaloGroup" class="zalo-group-section">
            <h4>Nhóm hỗ trợ chuyến đi</h4>
            
            <div class="important-notice">
              <div class="notice-header">
                <i class="fas fa-exclamation-circle"></i>
                <span>LƯU Ý QUAN TRỌNG</span>
              </div>
              <ul class="notice-list">
                <li>Chúng tôi <strong>sử dụng nhóm Zalo</strong> để cập nhật thông tin chuyến đi.</li>
                <li>Vui lòng <strong>tham gia nhóm</strong> để nhận lịch trình, hướng dẫn và thông báo thay đổi.</li>
                <li>BTC <strong>không chịu trách nhiệm</strong> nếu quý khách bỏ lỡ thông tin quan trọng do không tham gia nhóm.</li>
              </ul>
              <p class="notice-footer">Trân trọng cảm ơn quý khách!</p>
            </div>

            <!-- Add URL display with copy button -->
            <div class="zalo-url-container">
              <input
                type="text"
                readonly
                :value="zaloGroupLink"
                class="zalo-url-display"
                ref="zaloUrlInput"
              />
              <button @click="copyZaloLink" class="copy-btn">
                <i :class="['fas', isCopied ? 'fa-check' : 'fa-copy']"></i>
                <span>{{ isCopied ? 'Đã sao chép' : 'Sao chép' }}</span>
              </button>
            </div>

            <a :href="zaloGroupLink" target="_blank" class="zalo-link">
              <i class="fas fa-users"></i> Tham gia nhóm Zalo
            </a>
          </div>
        </div>
        <div v-else-if="isLoading" class="loading">
          <p>Đang tải thông tin...</p>
        </div>
        <div v-else class="error-message">
          <p>Không tìm thấy thông tin vé. Vui lòng liên hệ với chúng tôi.</p>
        </div>
      </div>

      <div class="buttons">
        <!-- <button class="download-btn" @click="downloadTicket">
          <i class="fas fa-download"></i> Tải vé
        </button> -->
        <button class="home-btn" @click="goHome">Quay về trang chủ</button>
      </div>
    </div>
    </FadeContent>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import axiosInstance from '@/plugins/axios'
import { decryptToken } from '@/utils/encryption'
import GradientText from '@/components/ui/GradientText.vue'
import { createCheckoutV2 } from '@/services/checkoutV2'
import { ownedPaymentState } from '@/services/checkoutV2Bridge'
import { CHECKOUT_V2_ENABLED } from '@/services/checkoutV2Flag'
import FadeContent from '@/components/ui/FadeContent.vue'

export default {
  name: 'PaymentSuccessView',
  components: { GradientText, FadeContent },
  props: {
    token: {
      type: String,
      required: true
    }
  },
  setup (props) {
    const router = useRouter()
    const isLoading = ref(true)
    // Off by default: the legacy path keeps its current behaviour until cutover.
    const verified = ref(!CHECKOUT_V2_ENABLED)
    const bookingInfo = ref([])
    const addonProducts = ref([]) // Thêm ref cho addon products
    const zaloGroupLink = ref('')
    const hasZaloGroup = computed(() => !!zaloGroupLink.value)
    const hasGroupBooking = ref(false) // Thêm ref để theo dõi group booking
    let tripZaloLink = null
    const isCopied = ref(false)
    const zaloUrlInput = ref(null)

    // Computed property tính tổng giá trị addon
    const totalAddonCost = computed(() => {
      return addonProducts.value.reduce((total, addon) => total + addon.total_price, 0)
    })

    const copyZaloLink = () => {
      if (!zaloUrlInput.value) return

      // Select the text
      zaloUrlInput.value.select()
      zaloUrlInput.value.setSelectionRange(0, 99999)

      // Copy to clipboard
      navigator.clipboard
        .writeText(zaloGroupLink.value)
        .then(() => {
          isCopied.value = true

          // Reset copied state after 2 seconds
          setTimeout(() => {
            isCopied.value = false
          }, 2000)
        })
        .catch(err => {
          console.error('Failed to copy: ', err)
        })
    }

    const isSpecialTicketTypeForBooking = booking => {
      if (!booking || !booking.ticket_type) {
        return false
      }
      return booking.ticket_type.independent === true
    }

    const formatTime = timestamp => {
      if (!timestamp) return ''
      return new Date(timestamp).toLocaleDateString('vi-VN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const formatCurrency = amount => {
      if (!amount) return 'N/A'
      return new Intl.NumberFormat('vi-VN', {
        style: 'currency',
        currency: 'VND'
      }).format(amount)
    }

    const loadBookingData = async () => {
      if (CHECKOUT_V2_ENABLED) {
        // Confirm with the server before the page claims anything was paid.
        try {
          const owned = await ownedPaymentState(createCheckoutV2())
          verified.value = Boolean(owned && owned.view.state === 'paid')
        } catch {
          verified.value = false
        }
        if (!verified.value) {
          isLoading.value = false
          return
        }
      }

      try {
        isLoading.value = true
        // Decrypt token to get booking IDs
        const decryptedBookingIds = decryptToken(props.token)
        if (!decryptedBookingIds) {
          throw new Error('Invalid booking token')
        }

        // Clean up the decrypted string by removing any quotes
        const cleanDecryptedIds = decryptedBookingIds.replace(/["']/g, '')
        const bookingIds = cleanDecryptedIds.split(',')

        if (!bookingIds.length) {
          throw new Error('No valid booking IDs found')
        }

        // Create an array to hold all booking information
        const allBookings = []
        let groupId = null

        // Fetch details for each booking
        for (const bookingId of bookingIds) {
          // Skip empty IDs
          if (!bookingId.trim()) continue

          // Fetch booking details
          const response = await axiosInstance.get(`/bookings/`, {
            params: { booking_id: bookingId.trim() }
          })

          const booking = Array.isArray(response.data)
            ? response.data[0]
            : response.data

          if (!booking) continue

          // Kiểm tra booking group
          if (booking.booking_group_id) {
            groupId = booking.booking_group_id
            hasGroupBooking.value = true
          }

          // Fetch trip details to get destination
          const tripResponse = await axiosInstance.get(`/trips/`, {
            params: { trip_id: booking.trip_id }
          })

          const trip = Array.isArray(tripResponse.data)
            ? tripResponse.data[0]
            : tripResponse.data

          if (trip && trip.zalo_group_link) {
            tripZaloLink = trip.zalo_group_link
          }

          // Get route info to find destination
          const routeResponse = await axiosInstance.get(`/routes/${trip.route_id}`)

          const route = Array.isArray(routeResponse.data)
            ? routeResponse.data[0]
            : routeResponse.data

          // Add this booking to our array
          allBookings.push({
            booking_id: booking.booking_id,
            destination: route.destination_location,
            departure_time: trip.departure_datetime,
            ticket_type: booking.ticket_type,
            seat_numbers: booking.slot_number ? [booking.slot_number] : [],
            customer_name: booking.customer_name,
            customer_phone: booking.customer_phone,
            price: booking.ticket_type?.price
          })
        }

        // Update the bookingInfo ref with all bookings
        bookingInfo.value = allBookings

        // Fetch addon products cho booking IDs
        await loadAddonProducts(bookingIds)

        if (tripZaloLink) {
          zaloGroupLink.value = tripZaloLink
        }
      } catch (error) {
        console.error('Error loading booking data:', error)
      } finally {
        isLoading.value = false
      }
    }

    // Thêm hàm load addon products
    const loadAddonProducts = async (bookingIds) => {
      try {
        // Gọi API lấy addon products cho các booking IDs
        const addonResponse = await axiosInstance.get(`/booking-addons/`, {
          params: { booking_ids: bookingIds.join(',') }
        })

        const addons = addonResponse.data

        if (addons && addons.length > 0) {
          // Xử lý dữ liệu addon để thêm thông tin bổ sung
          const processedAddons = []

          for (const addon of addons) {
            try {
              // Lấy thông tin booking để có tên người đặt
              const bookingResponse = await axiosInstance.get(`/bookings/`, {
                params: { booking_id: addon.booking_id }
              })

              const addonBooking = Array.isArray(bookingResponse.data)
                ? bookingResponse.data[0]
                : bookingResponse.data

              processedAddons.push({
                ...addon,
                total_price: addon.price * addon.quantity,
                ordered_by: addonBooking ? addonBooking.customer_name : 'Unknown'
              })
            } catch (bookingError) {
              console.error(`Error fetching booking ${addon.booking_id}:`, bookingError)
              // Thêm addon với thông tin mặc định nếu không lấy được booking
              processedAddons.push({
                ...addon,
                total_price: addon.price * addon.quantity,
                ordered_by: 'Unknown'
              })
            }
          }

          addonProducts.value = processedAddons
        }
      } catch (error) {
        console.error('Error loading addon products:', error)
        // Không throw error để không ảnh hưởng đến việc load booking chính
      }
    }

    const downloadTicket = () => {
      // Placeholder for ticket download functionality
      alert('Chức năng tải vé đang được phát triển')
    }

    const goHome = () => {
      router.push('/')
    }

    onMounted(() => {
      loadBookingData()
    })

    return {
      isLoading,
      verified,
      bookingInfo,
      addonProducts, // Export addon products
      totalAddonCost, // Export computed total
      hasGroupBooking, // Export group booking status
      formatTime,
      formatCurrency,
      isSpecialTicketTypeForBooking,
      downloadTicket,
      goHome,
      zaloGroupLink,
      hasZaloGroup,
      isCopied,
      zaloUrlInput,
      copyZaloLink
    }
  }
}
</script>

<style lang="scss" scoped>
.success-container {
  max-width: 800px;
  margin: 3rem auto;
  padding: 0 1.5rem;
  position: relative;
}

.success-bg-particles {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  background:
    radial-gradient(ellipse at 20% 20%, rgba(72, 187, 120, 0.06) 0%, transparent 50%),
    radial-gradient(ellipse at 80% 80%, rgba(56, 161, 105, 0.04) 0%, transparent 50%);
}

.success-card {
  position: relative;
  z-index: 1;
  background: white;
  border-radius: 1rem;
  padding: 2.5rem;
  text-align: center;
  box-shadow: 0 5px 30px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(72, 187, 120, 0.1);
}

.success-icon {
  font-size: 5rem;
  color: #16a34a;
  margin-bottom: 1.5rem;
  position: relative;
  display: inline-block;
}

.success-icon-ring {
  position: absolute;
  inset: -10px;
  border: 2px solid rgba(72, 187, 120, 0.2);
  border-radius: 50%;
  animation: pulse-ring 2s ease-out infinite;
}

@keyframes pulse-ring {
  0% { transform: scale(0.8); opacity: 1; }
  100% { transform: scale(1.4); opacity: 0; }
}

.success-icon i {
  animation: scaleIn 0.5s ease-out;
}

h1 {
  color: #064e3b;
  font-size: 2rem;
  margin-bottom: 1rem;
}

p {
  color: #075985;
  font-size: 1.125rem;
  margin-bottom: 2rem;
}

.booking-details {
  background: #f7fafc;
  border-radius: 0.75rem;
  padding: 1.5rem;
  margin-bottom: 2rem;

  h3 {
    font-size: 1.25rem;
    color: #064e3b;
    margin-bottom: 1rem;
    text-align: left;
  }
}

.booking-info {
  text-align: left;

  p {
    margin-bottom: 0.75rem;
    font-size: 1rem;

    strong {
      color: #064e3b;
      font-weight: 600;
      min-width: 120px;
      display: inline-block;
    }
  }
}

/* Styles cho addon section */
.addon-section {
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 0.75rem;
  padding: 1.5rem;
  margin-top: 2rem;
  text-align: left;

  h3 {
    color: #075985;
    margin-bottom: 1rem;
    text-align: center;
  }
}

.group-notice {
  background: #fef3c7;
  border: 1px solid #0ea5e9;
  border-radius: 0.5rem;
  padding: 0.75rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-style: italic;
  color: #92400e;

  i {
    color: #0ea5e9;
  }
}

.addon-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.addon-item {
  background: white;
  border: 1px solid #e0e7ff;
  border-radius: 0.5rem;
  padding: 1rem;
  border-left: 3px solid #3b82f6;

  .addon-header {
    margin-bottom: 0.75rem;

    h4 {
      color: #075985;
      font-weight: bold;
      margin: 0;
      font-size: 1.1rem;
    }
  }

  .addon-details {
    p {
      margin-bottom: 0.5rem;
      font-size: 0.9rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      color: #4b5563;

      i {
        width: 16px;
        color: #6b7280;
      }

      strong {
        color: #1f2937;
      }

      .price {
        font-weight: bold;
        color: #059669;
      }

      .total-price {
        font-weight: bold;
        color: #dc2626;
        font-size: 1rem;
      }
    }

    .ordered-by {
      font-style: italic;
      color: #6b7280;
      font-size: 0.85rem;
      margin-top: 0.5rem;
      padding-top: 0.5rem;
      border-top: 1px solid #e5e7eb;
    }
  }
}

.addon-total {
  margin-top: 1.5rem;
  padding: 1rem;
  background: #dcfce7;
  border-radius: 0.5rem;
  text-align: center;
  border: 1px solid #86efac;

  .total-amount {
    color: #059669;
    font-size: 1.25rem;
  }
}

.loading {
  padding: 1rem;
  color: #075985;
}

.buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;

  button {
    padding: 0.875rem 1.5rem;
    border-radius: 0.5rem;
    font-weight: 500;
    cursor: pointer;
    border: none;
    transition: all 0.2s;
  }

  .download-btn {
    background: linear-gradient(to right, #0ea5e9, #0284c7);
    color: white;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(66, 153, 225, 0.3);
    }
  }

  .home-btn {
    background: #e2e8f0;
    color: #075985;

    &:hover {
      background: #cbd5e0;
      transform: translateY(-2px);
    }
  }
}

/* Existing styles for other sections... */
.booking-item {
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 1rem;
  margin-bottom: 1rem;
  background-color: white;
}

.booking-item h4 {
  margin: 0 0 0.5rem;
  color: #0284c7;
  font-size: 1.1rem;
  border-bottom: 1px solid #edf2f7;
  padding-bottom: 0.5rem;
}

.booking-item:last-child {
  margin-bottom: 0;
}

.special-ticket-note {
  background-color: #feebc8;
  padding: 0.5rem;
  border-radius: 0.3rem;
  display: flex;
  align-items: center;
  margin-top: 0.5rem;
  font-size: 0.9rem;
  color: #9a3412;
}

.special-ticket-note i {
  margin-right: 0.5rem;
  color: #dd6b20;
}

.zalo-group-section {
  margin-top: 2rem;
  background-color: #e6f7ff;
  border-radius: 0.5rem;
  padding: 1rem;
  border: 1px solid #91d5ff;
  text-align: center;
}

.zalo-group-section h4 {
  color: #0066cc;
  margin-bottom: 0.5rem;
}

.zalo-link {
  display: inline-flex;
  align-items: center;
  background-color: #0068ff;
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 2rem;
  margin-top: 0.75rem;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.2s;
}

.zalo-link:hover {
  background-color: #0056d6;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 104, 255, 0.3);
}

.zalo-link i {
  margin-right: 0.5rem;
}

.zalo-url-container {
  display: flex;
  align-items: center;
  max-width: 450px;
  margin: 1rem auto;
  background-color: white;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #d9d9d9;
}

.zalo-url-display {
  flex: 1;
  padding: 10px 12px;
  border: none;
  font-size: 0.9rem;
  color: #333;
  background: white;
  outline: none;
  width: 100%;
  text-overflow: ellipsis;
}

.copy-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  background-color: #f0f0f0;
  border: none;
  padding: 10px 15px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 500;
  color: #333;
  border-left: 1px solid #d9d9d9;
  transition: all 0.2s;
}

.copy-btn:hover {
  background-color: #e6e6e6;
}

.copy-btn i {
  font-size: 0.9rem;
}

.copy-btn .fa-check {
  color: #22c55e;
}

.important-notice {
  background-color: #fff4e6;
  border: 1px solid #ffc069;
  border-radius: 0.5rem;
  padding: 1rem;
  margin-top: 1rem;
  text-align: left;
}

.notice-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: #d48806;
  margin-bottom: 0.5rem;
}

.notice-header i {
  font-size: 1.2rem;
}

.notice-list {
  list-style: none;
  padding: 0;
  margin: 0;
  color: #874d00;
  font-size: 0.95rem;
}

.notice-list li {
  margin-bottom: 0.5rem;
}

.notice-footer {
  margin-top: 1rem;
  font-size: 0.9rem;
  color: #874d00;
  text-align: center;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes scaleIn {
  from {
    transform: scale(0.8);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

@media (max-width: 640px) {
  .success-card {
    padding: 1.5rem;
  }

  .buttons {
    flex-direction: column;

    button {
      width: 100%;
    }
  }

  .addon-item {
    .addon-details p {
      font-size: 0.85rem;
    }
  }
}
</style>
